import { z } from "zod";
import { t } from "@lingui/core/macro";

// Messages are lazy (`error: () => ...`) so they are translated when validation
// runs, in the language active then, not once when this module loads.

export const createMenuItemRecipeSchema = z.object({
  name: z
    .string()
    .min(1, { error: () => t`Recipe name is required` })
    .max(200),
  servings: z.number().int().min(1).optional(),
  prepTimeMinutes: z.number().int().min(0).optional(),
  cookTimeMinutes: z.number().int().min(0).optional(),
});

export type CreateMenuItemRecipeFormData = z.infer<typeof createMenuItemRecipeSchema>;

/** Yield-pair errors; the dialog shows them translated, so they are matched, not shown raw. */
export const YIELD_UNIT_REQUIRED = "Choose the unit the batch is measured in";
export const YIELD_AMOUNT_REQUIRED = "Enter how much one batch makes";

export const createPrepRecipeSchema = z.object({
  name: z
    .string()
    .min(1, { error: () => t`Recipe name is required` })
    .max(200),
  servings: z.number().int().min(1).optional(),
  prepTimeMinutes: z.number().int().min(0).optional(),
  cookTimeMinutes: z.number().int().min(0).optional(),
  /** What one batch makes, e.g. 1.2 L of sauce; a prep recipe is costed per this unit. */
  totalYield: z.number().positive().optional(),
  yieldUnit: z.string().optional(),
}).superRefine((data, ctx) => {
  // The API takes a yield as a pair and drops half of one, so a batch size
  // without its unit (or the reverse) is caught here rather than lost.
  if (data.totalYield != null && !data.yieldUnit) {
    ctx.addIssue({ code: "custom", path: ["yieldUnit"], message: YIELD_UNIT_REQUIRED });
  }
  if (data.totalYield == null && data.yieldUnit) {
    ctx.addIssue({ code: "custom", path: ["totalYield"], message: YIELD_AMOUNT_REQUIRED });
  }
});

export type CreatePrepRecipeFormData = z.infer<typeof createPrepRecipeSchema>;

export const createRecipeSchema = z.object({
  name: z
    .string()
    .min(1, { error: () => t`Recipe name is required` })
    .max(200),
  // No default. A recipe that is quietly born PREP is a recipe that silently
  // never reaches a menu, so the chef has to say which kind this is (T-701).
  recipeType: z.enum(["PREP", "PLATED"], {
    error: () => t`Recipe type is required`,
  }),
  totalYield: z.number().min(0).optional(),
  yieldUnit: z.string().optional(),
});

export type CreateRecipeFormData = z.infer<typeof createRecipeSchema>;

export const exportRecipesSchema = z.object({
  includeMenuItems: z.boolean(),
  includePrepRecipes: z.boolean(),
  includeIngredients: z.boolean(),
  fileType: z.enum(["xlsx", "csv"]),
  includeImages: z.boolean().optional(),
  includeIngredientBreakdown: z.boolean().optional(),
  /** IDs of selected menu-item recipes to export. Empty = export all. */
  selectedMenuItemIds: z.array(z.number()).optional(),
  /** IDs of selected prep-recipe recipes to export. Empty = export all. */
  selectedPrepRecipeIds: z.array(z.number()).optional(),
});

export type ExportRecipesFormData = z.infer<typeof exportRecipesSchema>;
